<template>
    <div class="password-input">
        <AccountInput
            :errorMessage="errorMessage"
            :title="hideTitle ? null : 'Vytvořte heslo'"
            :name="name"
            @inputErrorDetected="emit('inputErrorDetected', name, errorMessage)"
        >
            <div class="password-input__container">
                <input
                    :type="inputType"
                    v-model="passwordValue"
                    :id="name"
                    @keyup="validPassword"
                    :placeholder="placeholder"
                    :autocomplete="autocompleteOff ? 'off' : null"
                />
                <KasaIcon
                    name="eye"
                    :width="20"
                    :height="20"
                    class="password-input__input-icon"
                    @click="showPassword"
                ></KasaIcon>
            </div>
        </AccountInput>

        <div class="password-input__check-list">
            <div class="password-input__check" :class="{ checked: checkList.specialCharacters }">
                Nesmí obsahovat diakritiku či speciální znaky
            </div>
            <div class="password-input__check" :class="{ checked: checkList.number }">
                Musí obsahovat alespoň 1 číslici a&nbsp;písmeno.
            </div>
            <div class="password-input__check" :class="{ checked: checkList.length }">
                Musí mít délku alespoň 6 znaků.
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import KasaIcon from "@/components/shared/assets/kasa-icon.vue";
import AccountInput from "./account-input.vue";

/* eslint-disable @typescript-eslint/naming-convention */
const emit = defineEmits<{
    "update:modelValue": [newValue: string];
    inputErrorDetected: [fieldName: string, errorMessage: string];
}>();

const props = defineProps<{
    modelValue: string;
    name: string;
    dataLayerErrorLogger?: (fieldName: string, message: string) => void;
    placeholder?: string;
    autocompleteOff?: boolean;
    hideTitle?: boolean;
}>();

const errorMessage = defineModel<string>("errorMessage");

const passwordValue = computed({
    get() {
        return props.modelValue;
    },
    set(passwordValue) {
        errorMessage.value = null;
        emit("update:modelValue", passwordValue);
    },
});

const eyeActive = ref(false);
const showPassword = async () => {
    eyeActive.value = !eyeActive.value;
};

const inputType = computed(() => (eyeActive.value ? "text" : "password"));

const checkPasswordSpecialCharacters = () => /\W+/.test(passwordValue.value);
const checkPasswordNumberLetter = () => /\d+/.test(passwordValue.value) && /[A-Za-z]+/.test(passwordValue.value);
const checkPasswordLength = () => passwordValue.value.length >= 6;

const checkList = reactive({
    specialCharacters: true,
    number: false,
    length: false,
});

const isNewPasswordValid = computed(() => checkList.specialCharacters && checkList.number && checkList.length);

const validPassword = () => {
    checkList.specialCharacters = !checkPasswordSpecialCharacters();
    checkList.number = checkPasswordNumberLetter();
    checkList.length = checkPasswordLength();

    return isNewPasswordValid.value;
};

const checkIfIsValid = (silentCheck?: boolean): boolean => {
    let isValid = true;

    if (!passwordValue.value) {
        if (!silentCheck) {
            errorMessage.value = "Nastavte si vaše heslo.";
        }
        isValid = false;
    } else if (!isNewPasswordValid.value) {
        if (!silentCheck) {
            errorMessage.value = "Heslo není validní.";
        }
        isValid = false;
    }

    return isValid;
};

defineExpose({
    checkIfIsValid,
});
</script>
